import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, guard } from '../services/auth.api'
import { randomUuid, User } from '@icure/cardinal-sdk'

export const userApiRtk = createApi({
  reducerPath: 'userApi',
  tagTypes: ['User'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    getUser: builder.query<User | undefined, string>({
      async queryFn(id, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<User> => {
          const user = await userApi.getUser(id)
          if (!user) {
            throw new Error('User does not exist')
          }
          return user
        })
      },
      providesTags: (res) => (res ? [{ type: 'User', id: res.id }] : []),
    }),
    createUser: builder.mutation<User | undefined, { email: string; id: string; name: string }>({
      async queryFn({ email, id, name }, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<User> => {
          const createdUser = await userApi.createUser(
            new User({
              id: randomUuid(),
              name: name,
              email: email,
              patientId: id,
            }),
          )
          if (!createdUser) {
            throw new Error('User does not exist')
          }
          return new User(createdUser)
        })
      },
      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'User', id: 'all' },
              { type: 'User', id: result.id },
            ]
          : [],
    }),
    modifyUser: builder.mutation<User | undefined, User>({
      async queryFn(userToModify, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<User> => {
          const updatedUser = await userApi.modifyUser(userToModify)
          if (!updatedUser) {
            throw new Error('User does not exist')
          }
          return updatedUser
        })
      },
      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'User', id: 'all' },
              { type: 'User', id: result.id },
            ]
          : [],
    }),
  }),
})

export const { useGetUserQuery, useCreateUserMutation, useModifyUserMutation } = userApiRtk
